import { useDispatch } from 'react-redux';
import { Status } from 'hooks/requests/fetchUtils';
import useFetchApi from 'hooks/useFetchApi';
import { useState, useCallback } from 'react';
import useQueryParameters from 'hooks/useQueryParameters';
import { setMagicLinkResult } from 'state/magicLink';
import { incrementAttempts } from 'state/otpCode';

const PATH = '/api/magic_link_verify';

type MagicLinkVerifyHook = () => [
  (params: MagicLinkVerifyRequest) => Promise<MagicLinkVerifyResult>,
  Status,
  boolean,
];

export interface MagicLinkVerifyRequest {
  magicLinkId: string;
  otpCode?: string;
}

export interface MagicLinkVerifyResult {
  phoneNumber: string;
  carrier: string;
  lineType: string;
  countryCode: string;
  phoneMatch: string;
  ipAddressMatch: boolean;
}

export class MagicLinkVerifyError extends Error {}
export class MagicLinkVerifyOtpError extends Error {}

const useMagicLinkVerify: MagicLinkVerifyHook = () => {
  const [error, setError] = useState<boolean>(false);
  const [status, fetch] = useFetchApi();
  const { mockMagicLinkVerify } = useQueryParameters();
  const dispatch = useDispatch();

  const magicLinkVerifyRequest = useCallback(
    async (params: MagicLinkVerifyRequest): Promise<MagicLinkVerifyResult> => {
      setError(false);

      const responsePromise = fetch(PATH, {
        method: 'POST',
        body: {
          ...params,
          mockResponse: mockMagicLinkVerify,
        },
      });
      const response = await responsePromise;

      if (response.ok) {
        const result: MagicLinkVerifyResult = await response.json();
        dispatch(setMagicLinkResult(result));
        return Promise.resolve(result);
      } else {
        dispatch(incrementAttempts());
        setError(true);
        if (response.status === 401) {
          return Promise.reject(new MagicLinkVerifyOtpError());
        } else {
          return Promise.reject(new MagicLinkVerifyError());
        }
      }
    },
    [dispatch, fetch, mockMagicLinkVerify],
  );

  return [magicLinkVerifyRequest, status, error];
};

export default useMagicLinkVerify;
